// ./src/utils/api/CommentService.ts
//
// Comments (roadmap #6) host service. OpResult-native. The organizer manages comments per event through
// eventsmagic-api's account-scoped resolvers (NOT the raw comments subgraph): list (per event / item /
// thread, with search by sender / text / date), enable comments on a place (ensure its channel), and
// remove a comment (moderation). Storage lives in the comments microservice; account scoping is enforced
// server-side from the JWT. See em-scaffold-ui + [[eventsmagic-comments]].
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// GraphQL
// =============================================================================

const EVENT_COMMENT_FIELDS = `
  id
  channelId
  channelCommentId
  eventId
  commenterName
  commenterKind
  visibility
  commentText
  hasReplies
  status
  createdAt
  updatedAt
  reactions { reaction count mine }
  attachments { id attachmentUrl attachmentMimeType }
  commenterPhoto { id fileUrl thumbnailUrl }
  commenterAvatarUrl
  mine
  editable
`;

const EVENT_COMMENT_LIST_QUERY = gql`
  query EventCommentList($filter: EventCommentFilter, $params: PaginationAndSorting) {
    eventCommentList(filter: $filter, params: $params) {
      code
      errors { name errors warnings }
      data { ${EVENT_COMMENT_FIELDS} }
    }
  }
`;

const EVENT_COMMENT_CHANNEL_ENSURE_MUTATION = gql`
  mutation EventCommentChannelEnsure($eventId: ID!, $entityKind: String, $entityId: ID, $channelName: String) {
    eventCommentChannelEnsure(eventId: $eventId, entityKind: $entityKind, entityId: $entityId, channelName: $channelName) {
      code
      errors { name errors warnings }
      data { id channelKey channelName channelAccessMode eventId entityKind entityId }
    }
  }
`;

const EVENT_COMMENT_REMOVE_MUTATION = gql`
  mutation EventCommentRemove($id: ID!) {
    eventCommentRemove(id: $id) {
      code
      errors { name errors warnings }
      data { id }
    }
  }
`;

const EVENT_COMMENT_POST_MUTATION = gql`
  mutation EventCommentPost(
    $eventId: ID!
    $entityKind: String
    $entityId: ID
    $commentText: String!
    $channelCommentId: ID
    $commenterName: String
    $visibility: String
  ) {
    eventCommentPost(
      eventId: $eventId
      entityKind: $entityKind
      entityId: $entityId
      commentText: $commentText
      channelCommentId: $channelCommentId
      commenterName: $commenterName
      visibility: $visibility
    ) {
      code
      errors { name errors warnings }
      data { ${EVENT_COMMENT_FIELDS} }
    }
  }
`;

const EVENT_COMMENT_EDIT_MUTATION = gql`
  mutation EventCommentEdit($eventId: ID, $channelCommentId: ID!, $commentText: String!) {
    eventCommentEdit(eventId: $eventId, channelCommentId: $channelCommentId, commentText: $commentText) {
      code
      errors { name errors warnings }
      data { ${EVENT_COMMENT_FIELDS} }
    }
  }
`;

const EVENT_COMMENT_REACT_MUTATION = gql`
  mutation EventCommentReact($eventId: ID, $channelCommentId: ID!, $reaction: String!) {
    eventCommentReact(eventId: $eventId, channelCommentId: $channelCommentId, reaction: $reaction) {
      code
      errors { name errors warnings }
      data { id reactions { reaction count mine } }
    }
  }
`;

const COMMENT_STREAM_TICKET_MUTATION = gql`
  mutation CommentStreamTicket($eventId: ID!) {
    commentStreamTicket(eventId: $eventId) {
      code
      errors { name errors warnings }
      data { ticket }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface EventCommentReaction {
  /** The emoji. */
  reaction: string;
  /** How many reactors used this emoji. */
  count: number;
  /** Whether the current host reacted with it. */
  mine: boolean;
}

export interface EventCommentAttachment {
  id?: string;
  attachmentUrl?: string | null;
  attachmentMimeType?: string | null;
}

export interface EventComment {
  id: string;
  channelId?: string | null;
  /** Parent comment id when this is a reply. */
  channelCommentId?: string | null;
  eventId?: string | null;
  commenterName?: string | null;
  /** guest | user. */
  commenterKind?: string | null;
  /** guest (everyone) | crew (host/crew-only). */
  visibility?: string | null;
  commentText?: string | null;
  hasReplies?: boolean | null;
  status?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  reactions?: EventCommentReaction[] | null;
  attachments?: EventCommentAttachment[] | null;
  /** A2e #91 — the author's stored photo (a guest's, as the event site shows them); null for none. */
  commenterPhoto?: { id?: string | null; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  /** A2e #91 — a ready picture URL when there is no stored photo: a guest's Gravatar, or a host's account picture. */
  commenterAvatarUrl?: string | null;
  /** Whether the current host authored the comment. */
  mine?: boolean | null;
  /** Whether the current host may edit it right now (theirs + still the newest comment in the thread). */
  editable?: boolean | null;
}

export interface EventCommentFilter {
  /** All comments for an event. */
  eventId?: string[] | null;
  /** A specific place/thread. */
  channelId?: string[] | null;
  /** A parent comment id → its replies; null → top-level only. */
  channelCommentId?: string | null;
  /** Search by sender — exact / partial (case-insensitive). */
  commenterName?: string | null;
  commenterNameLike?: string | null;
  /** guest | user. */
  commenterKind?: string[] | null;
  /** guest | crew. Omit to see both (host default). */
  visibility?: string[] | null;
  /** Free-text over the comment body. */
  commentTextLike?: string | null;
  /** Posted on/after (ISO). */
  createdFrom?: string | null;
  /** Posted on/before (ISO). */
  createdTo?: string | null;
  /**
   * #128/#133 — Load more: the oldest comment shown (`olderCursor` → beforeId + its createdAt `before`); the read is the
   * page older than it, inside createdFrom/createdTo. Set ONLY on a Load more, so the first page never sends a field the
   * gateway has not composed yet.
   */
  beforeId?: string;
  before?: string | null;
  status?: number[] | null;
}

export interface EventCommentChannel {
  id?: string;
  channelKey?: string | null;
  channelName?: string | null;
  channelAccessMode?: string | null;
  eventId?: string | null;
  entityKind?: string | null;
  entityId?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class CommentService {
  /** List comments (account-scoped server-side) per event / item / thread, with search. */
  static async list(filter?: EventCommentFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_COMMENT_LIST_QUERY, {
      filter,
      // PaginationAndSorting nests page/pageSize under `pagination` (NOT flat) — see paginationAndSorting.ts.
      params: { pagination, sortBy },
    });
  }

  /** Enable comments on an event place (POI / accommodation / guest-book / …) by ensuring its channel. */
  static async ensureChannel(
    eventId: string,
    entityKind?: string,
    entityId?: string,
    channelName?: string
  ): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_COMMENT_CHANNEL_ENSURE_MUTATION, {
      eventId,
      entityKind,
      entityId,
      channelName,
    });
  }

  /** Remove a comment (moderation; only within the caller's account). */
  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_COMMENT_REMOVE_MUTATION, { id });
  }

  /**
   * Post a comment or reply as the host/crew from an admin page (commenterKind=user). Pass
   * visibility='crew' to make it host/crew-only (guests never see it); default 'guest' (visible to everyone).
   */
  static async post(args: {
    eventId: string;
    commentText: string;
    entityKind?: string | null;
    entityId?: string | null;
    channelCommentId?: string | null;
    commenterName?: string | null;
    visibility?: "guest" | "crew" | null;
  }): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_COMMENT_POST_MUTATION, {
      eventId: args.eventId,
      entityKind: args.entityKind ?? null,
      entityId: args.entityId ?? null,
      commentText: args.commentText,
      channelCommentId: args.channelCommentId ?? null,
      commenterName: args.commenterName ?? null,
      visibility: args.visibility ?? "guest",
    });
  }

  /**
   * Mint a short-lived ticket to open the authenticated comment SSE stream for an owned event (roadmap #6b).
   * EventSource can't send an auth header, so the drawer calls this (JWT-authed) and opens
   * `/comments/stream?ticket=<ticket>`. The host/crew stream includes crew-only comments and works on drafts.
   */
  static async streamTicket(eventId: string): Promise<OpResult> {
    return ApiService.executeMutation(COMMENT_STREAM_TICKET_MUTATION, { eventId });
  }

  /** Edit your own comment's text — only while it's the newest comment in the thread (no one posted after). */
  static async edit(args: { eventId?: string | null; channelCommentId: string; commentText: string }): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_COMMENT_EDIT_MUTATION, {
      eventId: args.eventId ?? null,
      channelCommentId: args.channelCommentId,
      commentText: args.commentText,
    });
  }

  /** Toggle a reaction on a comment as host/crew; returns the comment's fresh reaction summary. */
  static async react(args: { eventId?: string | null; channelCommentId: string; reaction: string }): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_COMMENT_REACT_MUTATION, {
      eventId: args.eventId ?? null,
      channelCommentId: args.channelCommentId,
      reaction: args.reaction,
    });
  }
}

// A2e #91 — a leaf, so a guard can run it without loading the app's config.
export { commentAuthorPicture } from "./commentAuthorPicture";
